import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import ReportStepper from "../components/reportStepper";
import ReportFooter from "../components/reportFooter";
import SummaryTable from "../components/summaryTable";
import SuccessActions from "../components/successActions";
import GoBackLink from "../components/goBackLink";
import { confirmDialog } from "primereact/confirmdialog";
import { useNavigate, useParams } from "react-router-dom";
import useReport from "../hooks/useReport";
import {
  ReportStatus,
  ReportType,
  UpdateReportPayload,
  InternalReportStatusEnum,
} from "../api/models/report.models";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../types/ui-types";
import {
  ReportFormProvider,
  useReportFormContext,
} from "../contexts/reportFormContext";
import BasicReportForm from "../components/basicReportForm";
import BasicSupForm from "../components/basicSupForm";
import { useMonths } from "../hooks/useMonths";

const PaddedContainer = styled.div`
  text-align: left;
  flex-grow: 1;
`;

const Description = styled.p`
  font-size: 1rem;
  max-width: 700px;
  padding-left: 2rem;
`;

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
`;

const BasicReport: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [reportComment, setReportComment] = useState("");
  const [forceComment, setForceComment] = useState(false);
  const [isEmptyReport, setIsEmptyReport] = useState(false);

  const [
    companyId,
    organizationId,
    companyName,
    organizationName,
    selectedYearForNewReport,
    selectedMonthForNewReport,
    selectedQuarterForNewReport,
  ] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.organizationId,
      state.companyName,
      state.organizationName,
      state.selectedYearForNewReport,
      state.selectedMonthForNewReport,
      state.selectedQuarterForNewReport,
    ]),
  );
  const [activeStep, setActiveStep] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSupFormDirty, setIsSupFormDirty] = useState(false);
  const saveDraftRef = useRef<(() => Promise<void>) | undefined>();
  const saveSupDraftRef = useRef<(() => Promise<void>) | undefined>();

  const { months } = useMonths();

  const {
    displayEcomodError,
    isUpdatingReportRow,
    showCreateProductView,
    showEditReportRowView,
  } = useReportFormContext();

  const {
    reportData,
    isLoadingReport,
    updateExistingReport,
    isUpdatingReport,
  } = useReport({
    reportId: parseInt(reportId as string, 10),
  });

  // If report is already sent, navigate to the report view page, editing is not allowed
  useEffect(() => {
    if (
      reportData &&
      reportData.reportStatus === ReportStatus.Sent &&
      activeStep !== 3
    ) {
      navigate(`/view-report/${reportId}`);
    }
  }, [reportData]);

  const handleStepClick = async (stepIndex: number) => {
    // If report is sent, don't allow navigation away from final step
    if (reportData?.reportStatus === ReportStatus.Sent && stepIndex !== 3) {
      return;
    }

    // Auto-save if user has made changes in step 1 and is trying to navigate away
    if (
      activeStep === 0 &&
      stepIndex !== 0 &&
      isFormDirty &&
      saveDraftRef.current
    ) {
      try {
        await saveDraftRef.current();
        setIsFormDirty(false); // Reset dirty state after successful save
      } catch (error) {
        console.error("Failed to save draft:", error);
        // If save fails, don't navigate away
        return;
      }
    }

    // Auto-save if user has made changes in step 2 and is trying to navigate away
    if (
      activeStep === 1 &&
      stepIndex !== 1 &&
      isSupFormDirty &&
      saveSupDraftRef.current
    ) {
      try {
        await saveSupDraftRef.current();
        setIsSupFormDirty(false); // Reset dirty state after successful save
      } catch (error) {
        console.error("Failed to save SUP draft:", error);
        // If save fails, don't navigate away
        return;
      }
    }

    // Allow free movement between steps 0-2 (steps 1-3 in UI)
    if (stepIndex >= 0 && stepIndex <= 2) {
      setActiveStep(stepIndex);
    }

    // Don't allow manual navigation to final step (step 3) - this should only be reached through the process
    if (stepIndex === 3 && reportData?.reportStatus !== ReportStatus.Sent) {
      return;
    }
  };

  const handleNextStep = async () => {
    if (activeStep === 0) {
      confirmDialog({
        showHeader: false,
        message: t("message-have-you-confirmed-your-data"),
        acceptLabel: t("cmd-continue"),
        rejectLabel: t("cmd-go-back"),
        accept: () => setActiveStep((prevStep) => prevStep + 1),
        style: { minWidth: "300px" },
      });
    }

    if (activeStep === 1) {
      /* alert("Sup step not implemented yet"); */
      setActiveStep((prevStep) => prevStep + 1);
    }

    if (activeStep === 2) {
      let payload: UpdateReportPayload;
      try {
        payload = {
          id: parseInt(reportId as string, 10),
          companyId: companyId as number,
          organizationId: organizationId || undefined,
          year: reportData?.year as number,
          quarter: reportData?.quarter as number,
          month: reportData?.month as number,
          reportStatus: organizationId
            ? ReportStatus.WaitingForApproval
            : ReportStatus.Sent,
          comment: reportComment || undefined,
          reportType: reportData?.reportType || ReportType.DETAILED,
          rows: reportData?.rows || [],
          internalReportStatus: isEmptyReport
            ? InternalReportStatusEnum.ToBeReviewed
            : undefined,
        };
      } catch (error) {
        console.error("Error creating payload:", error);
        return;
      }
      await updateExistingReport(payload);
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const getNextStepLabel = () => {
    switch (activeStep) {
      case 0:
        return t("cmd-continue");
      case 1:
        return t("cmd-continue");
      case 2:
        return t("cmd-send");
      default:
        return "";
    }
  };

  const getContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <BasicReportForm
            reportId={reportId ? parseInt(reportId as string, 10) : undefined}
            reportData={reportData}
            isLoadingReport={isLoadingReport}
            handleNextStep={handleNextStep}
            onFormDirtyChange={setIsFormDirty}
            saveDraftRef={saveDraftRef}
          />
        );
      case 1:
        return (
          <BasicSupForm
            reportId={reportId ? parseInt(reportId as string, 10) : undefined}
            reportData={reportData}
            isLoadingReport={isLoadingReport}
            handleNextStep={handleNextStep}
            onFormDirtyChange={setIsSupFormDirty}
            saveDraftRef={saveSupDraftRef}
          />
        );

      case 2:
        return (
          <SummaryTable
            reportComment={reportComment}
            setReportComment={setReportComment}
            forceComment={forceComment}
            setForceComment={setForceComment}
            setIsEmptyReport={setIsEmptyReport}
          />
        );
      case 3:
        return <SuccessActions reportId={parseInt(reportId as string, 10)} />;
      default:
        return null;
    }
  };

  const getDescription = () => {
    switch (activeStep) {
      case 0:
        return t("description-creating-detailed-report");
      case 1:
        return t("description-sup-info");
      case 2:
        return t("description-summary");
      case 3:
        return t("description-report-sent-successfully");
      default:
        return "";
    }
  };

  const getViewActions = () => {
    switch (activeStep) {
      case 0:
        return null;
      case 1:
        return null;
      default:
        return null;
    }
  };

  const getViewHeader = () => {
    switch (activeStep) {
      case 0:
        return t("header-create-report");
      case 1:
        return t("header-sup-info");
      case 2:
        return t("header-summary");
      case 3:
        return t("header-report-complete");
      default:
        return t("header-create-report");
    }
  };

  const getReportInfo = () => {
    if (reportData) {
      if (reportData.month) {
        return months[reportData.month - 1] + " " + reportData.year;
      }
      return `${reportData.year}${
        reportData.quarter ? ` Q${reportData.quarter}` : ""
      }`;
    } else {
      if (selectedYearForNewReport) {
        if (selectedMonthForNewReport) {
          return (
            months[selectedMonthForNewReport - 1] +
            " " +
            selectedYearForNewReport
          );
        }
        return `${selectedYearForNewReport}${
          selectedQuarterForNewReport ? ` Q${selectedQuarterForNewReport}` : ""
        }`;
      }
    }
    return "";
  };

  const getCompanyOrOrganizationName = () => {
    return organizationName || companyName || "";
  };

  return (
    <ReportContainer className="report-container">
      <ReportStepper
        activeStep={activeStep}
        steps={[
          t("label-fill-basic-information"),
          t("label-fill-sup-information"),
          t("label-summary"),
          t("label-complete"),
        ]}
        onStepClick={handleStepClick}
        disableNavigation={reportData?.reportStatus === ReportStatus.Sent}
      />
      <PaddedContainer>
        {getViewActions()}
        <h1 style={{ padding: "2rem 2rem 0 2rem" }}>{getViewHeader()}</h1>
        <h4
          style={{
            padding: "0 2rem 1rem 2rem",
            fontWeight: "normal",
            marginBottom: 0,
          }}
        >
          {getReportInfo() + " - " + getCompanyOrOrganizationName()}
        </h4>

        <Description>{getDescription()}</Description>

        {activeStep > 0 && activeStep < 3 && (
          <GoBackLink
            onClick={() => {
              setActiveStep((prevStep) => prevStep - 1);
            }}
          >
            {t("go-back") || "Go back"}
          </GoBackLink>
        )}

        {getContent()}
      </PaddedContainer>
      {activeStep === 2 && !showCreateProductView && !showEditReportRowView && (
        <ReportFooter
          handleNextStep={handleNextStep}
          nextStepLabel={getNextStepLabel()}
          showConfirmationCheckbox={false}
          proceedDisabled={!reportData || reportData.rows.length === 0 || displayEcomodError} // for EAN reporting
          displayEcomodError={displayEcomodError}
          isLoading={isUpdatingReportRow || isUpdatingReport || isLoadingReport}
          disableNextButton={forceComment && reportComment.trim() === ""} // require comment if changes detected
        />
      )}
    </ReportContainer>
  );
};

export default () => (
  <ReportFormProvider>
    <BasicReport />
  </ReportFormProvider>
);
